import { API_SIGNATURE_SECRET } from '@/config/env';

/**
 * Stable JSON stringify with sorted keys for consistent hashing
 */
function stableStringify(obj: any): string {
  if (obj === null || obj === undefined) return '';
  if (typeof obj !== 'object') return JSON.stringify(obj);

  if (Array.isArray(obj)) {
    return `[${obj.map(stableStringify).join(',')}]`;
  }

  const keys = Object.keys(obj).sort();
  const pairs = keys.map(key => `"${key}":${stableStringify(obj[key])}`);
  return `{${pairs.join(',')}}`;
}

/**
 * SHA256 hash of a string
 */
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * HMAC-SHA256 signature
 */
async function hmacSha256(key: string, message: string): Promise<string> {
  const keyBuffer = new TextEncoder().encode(key);
  const messageBuffer = new TextEncoder().encode(message);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageBuffer);
  const hashArray = Array.from(new Uint8Array(signature));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate API signature headers
 */
export async function generateApiSignature(
  method: string,
  path: string,
  body?: any
): Promise<{ 'X-Request-Timestamp': string; 'X-Request-Signature': string } | null> {
  console.log('[API Signature] Secret exists:', !!API_SIGNATURE_SECRET, 'Secret value:', API_SIGNATURE_SECRET ? `${API_SIGNATURE_SECRET.substring(0, 4)}...` : 'EMPTY');
  if (!API_SIGNATURE_SECRET) {
    console.warn('[API Signature] No secret set — skipping signature');
    return null; // No signature required if secret not set
  }

  const timestamp = Math.floor(Date.now() / 1000).toString();
  const bodyHash = body ? await sha256(stableStringify(body)) : '';

  const canonicalString = `${method.toUpperCase()}\n${path}\n${timestamp}\n${bodyHash}`;

  const signature = await hmacSha256(API_SIGNATURE_SECRET, canonicalString);

  const headers = {
    'X-Request-Timestamp': timestamp,
    'X-Request-Signature': signature,
  };
  console.log('[API Signature] Generated:', { method, path, timestamp, canonicalString, signature: `${signature.substring(0, 16)}...` });
  return headers;
}